import { OrderPartnerReviewRatingEnum } from "Infrastructure/Api/Api";
import { useFieldError } from "Shared/Components/Form/Helpers/getFormErrors";
import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import {
	Box,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	type SxProps,
	type Theme,
	Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type {
	Control,
	FieldError,
	FieldErrors,
	FieldPath,
	FieldValues,
} from "react-hook-form";
import { Controller, get } from "react-hook-form";

interface LabelBaseProps {
	selected?: boolean;
}

interface StyledLabelProps extends LabelBaseProps {
	theme: Theme;
}

interface RadioReviewButtonsProps<T extends FieldValues> {
	id: string;
	name: FieldPath<T>;
	control: Control<T>;
	errors: FieldErrors;
	label?: string;
	sxLabel?: SxProps<Theme>;
}

const LabelBase = styled(Box, {
	shouldForwardProp: (prop) => prop !== "selected",
})<LabelBaseProps>(({ theme }) => ({
	border: `1px solid ${theme.palette.action.hover}`,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	cursor: "pointer",
	transition: "all 0.2s ease",
	fontSize: "1.5rem",
	width: 64,
	height: 64,
}));

const SelectedLabel = styled(LabelBase)(
	({ theme, selected }: StyledLabelProps) => ({
		...(selected && {
			borderColor: theme.palette.primary.main,
			backgroundColor: theme.palette.primary.light,
		}),
	}),
);

/**
 * Display review buttons that allows
 * select "satisfied" or "not satisfied"
 */
export function RadioReviewButtons<T extends FieldValues>({
	id,
	name,
	control,
	errors,
	label,
	sxLabel,
	...rest
}: RadioReviewButtonsProps<T>) {
	const { errorMessage } = useFieldError({
		fieldError: get(errors, name) as FieldError,
	});

	return (
		<Controller
			control={control}
			name={name}
			rules={{
				required: true,
			}}
			render={({ field }) => {
				const selectedValue = field.value;

				return (
					<>
						{label && (
							<CustomFormLabel sx={sxLabel} htmlFor={id}>
								{label}
							</CustomFormLabel>
						)}
						<RadioGroup id={id} row {...field} sx={{ gap: 2 }}>
							<FormControlLabel
								value={OrderPartnerReviewRatingEnum.satisfied}
								control={<Radio style={{ display: "none" }} />}
								label={
									<SelectedLabel
										selected={
											selectedValue === OrderPartnerReviewRatingEnum.satisfied
										}
									>
										👍
									</SelectedLabel>
								}
								sx={{ margin: 0 }}
							/>
							<FormControlLabel
								value={OrderPartnerReviewRatingEnum.not_satisfied}
								control={<Radio style={{ display: "none" }} />}
								label={
									<SelectedLabel
										selected={
											selectedValue ===
											OrderPartnerReviewRatingEnum.not_satisfied
										}
									>
										👎
									</SelectedLabel>
								}
								sx={{ margin: 0 }}
							/>
						</RadioGroup>
						{errorMessage && (
							<FormHelperText sx={{ ml: "1rem", mt: "3px", mb: 2 }}>
								<Typography color="error" variant="caption">
									{errorMessage}
								</Typography>
							</FormHelperText>
						)}
					</>
				);
			}}
			{...rest}
		/>
	);
}
